import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-grading-attendance-explained",
  title: "DU's Grading & 66.6% Attendance Rules: The Complete First-Year Survival Guide",
  description:
    "Exhaustive guide to Delhi University's grading and attendance system under UGCF 2022. CA (40) / IA (30) / Exam (90) mark splits, Ordinance VII 66.6% attendance floor, detention rules, medical concessions, and CGPA-to-percentage conversion formulas.",
  pubDate: "2026-05-27",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "college-life",
  tags: ["DU", "attendance", "grading", "first-year", "cgpa-formula", "ugcf-2022", "ordinance-vii"],
  readMinutes: 16,
  heroEyebrow: "College · Regulations & Ordinance Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Navigating Delhi University's academic environment requires understanding the official evaluation and attendance regulations published on the{" "}
        <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
          University of Delhi Main Portal
        </a>{" "}
        and administered by the{" "}
        <a href="https://exam.du.ac.in/" target="_blank" rel="noopener noreferrer">
          DU Examination Branch
        </a>
        . Misunderstandings surrounding credit weighting, continuous assessment, or Ordinance VII attendance thresholds can lead to unexpected exam detentions or lost CGPA points.
      </p>

      <h2>1. Internal vs. End-Semester Assessment Split under UGCF 2022</h2>
      <p>
        Under the{" "}
        <a
          href="https://www.du.ac.in/index.php?page=ugcf-2022"
          target="_blank"
          rel="noopener noreferrer"
        >
          DU Undergraduate Curriculum Framework (UGCF 2022)
        </a>
        , evaluation for a standard 4-credit course (160 total marks) is structured into three mandatory components:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Assessment Component</th>
              <th className="p-3 border-r border-rule">Marks Allocation</th>
              <th className="p-3">Evaluation Method &amp; Criteria</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Continuous Assessment (CA)</td>
              <td className="p-3 border-r border-rule font-semibold">40 Marks</td>
              <td className="p-3">Class tests, tutorial presentations, group assignments, attendance marks.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Internal Assessment (IA)</td>
              <td className="p-3 border-r border-rule font-semibold">30 Marks</td>
              <td className="p-3">Mid-term written test (20 marks) &amp; term papers / projects (10 marks).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">End-Semester Written Exam</td>
              <td className="p-3 border-r border-rule font-semibold">90 Marks</td>
              <td className="p-3">Centralized 3-hour written examination set by DU Examination Branch.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. Ordinance VII: The 66.6% Attendance Rule &amp; Detention Criteria</h2>
      <p>
        Delhi University Ordinance VII mandates a strict <strong>66.6% minimum attendance floor</strong> calculated individually for each enrolled subject. Failing to meet this threshold leads to detention from sitting the end-semester examination for that paper.
      </p>

      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Attendance Safe Buffer Formula</strong>
        <p className="mt-2 font-mono">
          Minimum Required Lectures = Math.ceil(0.666 × Total Conducted Lectures)
          <br /><br />
          <em>Worked Example:</em> If a course holds 60 lectures in a semester:
          <br />
          Required Attendance = Math.ceil(0.666 × 60) = 40 lectures.
          <br />
          Maximum Allowed Bunks = 60 - 40 = 20 lectures.
        </p>
      </div>

      <h3>Medical Concessions &amp; ECA Waivers</h3>
      <ul>
        <li>
          <strong>Medical Waiver (Up to 16.6% Relaxation):</strong> Students suffering from illness can submit a verified medical certificate from a registered medical practitioner within 7 days of returning to college. This can relax the attendance requirement from 66.6% down to 50%.
        </li>
        <li>
          <strong>ECA / NCC / Sports Duty Waiver:</strong> Students representing their college or university in official events receive attendance duty slips (ODs) which count as attended lectures.
        </li>
      </ul>

      <h2>3. Official CGPA to Percentage Conversion Formula</h2>
      <p>
        For official applications (scholarships, IIM CAT, UPSC Civil Services, or master's entry at DSE/JNU), Delhi University prescribes multiplying Cumulative Grade Point Average (CGPA) by 9.5:
      </p>

      <div className="my-6 border border-rule bg-card p-5 text-center">
        <span className="font-mono text-grade font-semibold text-lg">
          Equivalent Percentage (%) = Cumulative Grade Point Average (CGPA) × 9.5
        </span>
        <p className="text-pencil text-xs mt-2 font-mono">
          <em>Worked Example:</em> A student with a CGPA of 8.2 gets an equivalent percentage of 8.2 × 9.5 = 77.9%.
        </p>
      </div>

      <h2>4. Related Academic Guides</h2>
      <p>
        To understand how degree structures affect your transcript, explore our guides on{" "}
        <a href="/blog/honours-vs-programme" className="font-semibold underline text-grade">
          DU Honours vs BA Programme Differences
        </a>{" "}
        and{" "}
        <a href="/blog/du-long-answer-question-playbook" className="font-semibold underline text-grade">
          The DU Long-Answer Question Playbook
        </a>
        .
      </p>
    </>
  );
}


